/**
 * UseAICommands — the source for the chat input's slash-command palette.
 *
 * Reuses the same `GET /ai/catalog` endpoint the (removed) AICatalogDrawer read
 * (via the shared useAICatalog hook), then curates the raw tool metadata down
 * to the _user-facing_ capabilities worth surfacing as `/commands`: the
 * descriptor-derived "virtual" cards (Create/List objective, Export ICS 201, …)
 * and the data-source actions (web/file/incident search). The generic
 * create/update/list tools are excluded (they're already removed server-side by
 * CatalogRouter's virtual backing).
 *
 * Each command also carries a `group` (e.g. "Objectives", "ICS Form Exports",
 * "Data Sources") so the palette can render collapsible category sections.
 *
 * Selecting a command prefills `promptTemplate` into the input — it does not
 * invoke a tool directly; the agent still decides and runs through the normal
 * pipeline.
 */
import { useMemo } from 'react';
import { useFeatureFlags } from 'utils/featureFlags';
import { humanizeFieldName } from '../confirmationFieldConfig';
import {
  type AICatalog,
  type CatalogScenario,
  type CatalogSkill,
  type CatalogTool,
  useAICatalog,
} from './useAICatalog';

export type { AICatalog, CatalogScenario, CatalogSkill, CatalogTool };

export interface AICommand {
  /** Slash token shown/typed in the input, e.g. "/create-objective". */
  command: string;
  /** Human-readable label, e.g. "Create Objective". */
  label: string;
  description: string;
  category: string;
  /**
   * Top-level palette section: guided `skill`s (deepagents subagent workflows
   * and the scenario registry's one-click reviews) vs plain `tool`s.
   */
  kind: 'tool' | 'skill';
  /**
   * Collapsible sub-group heading within the top-level section, e.g.
   * "Objectives" / "ICS Form Exports" / "Data Sources" for tools, "Incident
   * Management" for skills.
   */
  group: string;
  /** Editable text inserted into the input when the command is picked. */
  promptTemplate: string;
  /**
   * Doctrine anchor for skills (e.g. "CG-IMH Ch 4 — Incident Briefing (ICS
   * 201)").
   */
  imhReference?: string;
  /**
   * When the skill applies (e.g. "OP 0 only"), shown so users know why a skill
   * may be unavailable.
   */
  entryConditions?: string;
  /**
   * The full registry entry behind a `review` command — carries the guidance
   * and the sample queries with expected responses that the palette's detail
   * strip and the examples view render.
   */
  scenario?: CatalogScenario;
  /**
   * User-facing data sources the capability uses (picker labels: "Incident",
   * "Files", "Web", …) — pills in the Learn-more dialog, bolded in the detail
   * strip.
   */
  dataSources?: string[];
}

const toSlashToken = (name: string): string => `/${name.replace(/_/g, '-')}`;

/**
 * Curation predicate: a catalog entry becomes a user-facing command when it is
 * a virtual card (`virtualOf` set — the create/list/update entity cards and
 * form exports) or a data-source action. The generic
 * create/update/list_ics_records tools are already filtered out server-side by
 * CatalogRouter, so this surfaces only the per-entity "virtual" cards.
 */
const isUserFacingCommand = (tool: CatalogTool): boolean =>
  tool.kind === 'data_source' || !!tool.virtualOf;

/**
 * Group heading for the palette's collapsible sections. ICS entity cards
 * (create_objective / list_objectives / update_objective) collapse to the
 * entity noun ("Objectives"); form exports group together; data sources group
 * together.
 */
const deriveGroup = (tool: CatalogTool): string => {
  if (tool.kind === 'data_source') {
    return 'Data Sources';
  }
  if (tool.category === 'ics_forms' || tool.virtualOf === 'export_ics_form') {
    return 'ICS Form Exports';
  }
  const entity = tool.name.match(/^(?:create|update|list)_(.+)$/);
  if (entity) {
    const singular = entity[1].replace(/s$/, '');
    return `${humanizeFieldName(singular)}s`;
  }
  return humanizeFieldName(tool.category);
};

/**
 * Sub-group heading for a skill within the "Skills" section. Every current
 * skill is incident-management; when a second skill domain lands, serve the
 * display group from the catalog (alongside `category`) instead of mapping
 * categories client-side.
 */
const SKILL_GROUP = 'Incident Management';

/**
 * Generic availability check against the server-declared `requiredFeatureFlag`
 * on catalog entries. The server decides WHICH entries need a flag (today:
 * everything QA hasn't verified carries IM_SKILLS); the client only tests the
 * declared requirement against the user's active flags. Skills are also
 * enforced server-side at agent construction, so this is presentation — not the
 * security boundary.
 */
const isFeatureEnabled = (
  entry: { requiredFeatureFlag?: string },
  activeFlags: readonly string[]
): boolean =>
  !entry.requiredFeatureFlag || activeFlags.includes(entry.requiredFeatureFlag);

/**
 * Guided skills surface under the "Skills" section. The prefilled prompt names
 * the skill explicitly — the backend's GUIDED SKILLS prompt section instructs
 * the agent to dispatch a skill the user names, so the slash command routes to
 * the skill subagent rather than leaving delegation to model judgment.
 * Descriptions render in the palette's detail strip (not the row), so they are
 * not truncated here.
 */
const skillToCommand = (skill: CatalogSkill): AICommand => ({
  command: toSlashToken(skill.name),
  label: humanizeFieldName(skill.name),
  description: skill.description.split('\n')[0],
  category: skill.category,
  kind: 'skill',
  group: SKILL_GROUP,
  promptTemplate: `Use the ${skill.name} skill: ${
    skill.examplePrompts?.[0] ?? humanizeFieldName(skill.name)
  }`,
  imhReference: skill.imhReference,
  entryConditions: skill.entryConditions,
  dataSources: skill.dataSources,
});

/**
 * Ui-bearing scenarios (the one-click reviews) surface under the "Skills"
 * section alongside the guided skills, sub-grouped by the entity they review
 * ("Objectives", …). The prefilled prompt is the scenario's full canned prompt;
 * the attached `scenario` carries guidance + examples for the detail strip and
 * the "Learn more" examples view.
 */
const scenarioToCommand = (scenario: CatalogScenario): AICommand => ({
  command: scenario.ui!.command,
  label: scenario.title,
  description: scenario.description,
  category: scenario.category,
  kind: 'skill',
  group: scenario.context
    ? humanizeFieldName(scenario.context.entity)
    : humanizeFieldName(scenario.category),
  promptTemplate: scenario.prompt,
  scenario,
  dataSources: scenario.dataSources,
});

// Top-level palette order: Skills first (they're the discovery target), then Tools.
const KIND_ORDER: Record<AICommand['kind'], number> = { skill: 0, tool: 1 };

/**
 * Pure catalog → command-list transform (exported for tests). Sorted by
 * top-level kind (Skills first), then sub-group, then label, so the palette's
 * two-level builder receives a stable, pre-ordered list.
 *
 * Skills and reviews are omitted in read-only 'ask' mode: the backend skips the
 * entire skill layer there, and the reviews propose writes the read-only agent
 * can't make — offering either would prefill a request the agent silently can't
 * honor.
 *
 * Entries whose server-declared `requiredFeatureFlag` is not in `activeFlags`
 * are omitted. Today the server stamps IM_SKILLS on everything QA hasn't
 * verified (the guided skills, five of the reviews, and all tool cards), so
 * without the flag only the verified reviews surface.
 */
export const buildCommands = (
  data?: AICatalog,
  mode?: string,
  activeFlags: readonly string[] = []
): AICommand[] => {
  // Tool-targeted example scenarios (no ui block) attach to their tool's
  // command so every row's "Learn more" shows a sample query + expected
  // response. Descriptor virtual cards fall back to their generic backing
  // tool's example via `virtualOf`.
  const scenarioByToolName = new Map<string, CatalogScenario>();
  for (const scenario of data?.scenarios ?? []) {
    if (scenario.target.kind === 'tool' && !scenario.ui) {
      if (!scenarioByToolName.has(scenario.target.toolName)) {
        scenarioByToolName.set(scenario.target.toolName, scenario);
      }
    }
  }

  const toolCommands: AICommand[] = (data?.tools ?? [])
    .filter((tool) => isFeatureEnabled(tool, activeFlags))
    .filter(isUserFacingCommand)
    .map((tool) => ({
      command: toSlashToken(tool.name),
      label: humanizeFieldName(tool.name),
      description: tool.description.split('\n')[0],
      category: tool.category,
      kind: 'tool',
      group: deriveGroup(tool),
      promptTemplate: tool.exampleInvocation ?? humanizeFieldName(tool.name),
      dataSources: tool.dataSources,
      scenario:
        scenarioByToolName.get(tool.name) ??
        (tool.virtualOf ? scenarioByToolName.get(tool.virtualOf) : undefined),
    }));

  // Same join for skill-targeted example scenarios.
  const scenarioBySkillName = new Map<string, CatalogScenario>();
  for (const scenario of data?.scenarios ?? []) {
    if (scenario.target.kind === 'skill' && !scenario.ui) {
      if (!scenarioBySkillName.has(scenario.target.skillName)) {
        scenarioBySkillName.set(scenario.target.skillName, scenario);
      }
    }
  }

  const skillCommands =
    mode === 'ask'
      ? []
      : (data?.skills ?? [])
          .filter((skill) => isFeatureEnabled(skill, activeFlags))
          .map((skill) => ({
            ...skillToCommand(skill),
            scenario: scenarioBySkillName.get(skill.name),
          }));

  const reviewCommands =
    mode === 'ask'
      ? []
      : (data?.scenarios ?? [])
          .filter((s) => s.ui)
          .filter((s) => isFeatureEnabled(s, activeFlags))
          .map(scenarioToCommand);

  return [...reviewCommands, ...skillCommands, ...toolCommands].sort(
    (a, b) =>
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      a.group.localeCompare(b.group) ||
      a.label.localeCompare(b.label)
  );
};

/**
 * @param mode The chat's agent mode ('ask' | 'agent_auto' | 'agent_confirm');
 *   skills are hidden in 'ask'. Catalog entries carrying a
 *   `requiredFeatureFlag` (today: everything not yet QA-verified, stamped
 *   IM_SKILLS server-side) surface only when that flag is active.
 */
export const useAICommands = (mode?: string): AICommand[] => {
  const { data } = useAICatalog();
  const activeFlags = useFeatureFlags();

  return useMemo(
    () => buildCommands(data, mode, activeFlags),
    [data, mode, activeFlags]
  );
};
